/**
 * Page caching, on an app's settings screen (apps/cloud's
 * services/page-cache.ts, 2026-10-26).
 *
 * WHAT THIS SCREEN HAS TO GET ACROSS: turning this on is safe only once the
 * app says which of its pages are the same for everyone. Cloudflare keeps a
 * page with no such marking for two hours and shows it to every visitor, so an
 * app that greets a signed-in person by name would greet the next person with
 * the same name. The app's code is the only place that marking can live, so the
 * screen hands the owner a sentence for their coding agent BEFORE the button,
 * the same way the bundle screen does, and says the risk in the sentence next
 * to the switch rather than in a tooltip.
 *
 * Turning it off is always one click and always safe.
 */

import { useState } from 'react';

import { Button } from '../primitives/Button';
import { CopyField } from './CopyField';
import { Notice } from './Notice';
import { SectionPanel } from './SectionPanel';

import { captureEvent } from '../../lib/analytics/posthog';
import { ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { setPageCache } from '../../lib/real-api';
import type { Project } from '../../lib/types';

/** The demo has no Worker to re-upload, so the switch flips locally. */
const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** Kept in English on purpose: it is handed to a coding agent, not read. */
const AGENT_ASK =
  'Set up page caching in this app the way SVAGENTS.md describes, then tell me whether it is safe to turn on.';

interface Props {
  project: Project;
  /** Paused apps refuse the change on the server; the button says so first. */
  paused: boolean;
  /** The project as the server answered the change, so the screen agrees. */
  onChanged: (updated: Project) => void;
}

export function PageCacheSection({ project, paused, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localOn, setLocalOn] = useState<boolean | null>(null);
  const on = localOn ?? project.pageCache ?? false;

  function toggle(next: boolean) {
    if (USE_MOCK) {
      setLocalOn(next);
      return;
    }
    setBusy(true);
    setError(null);
    captureEvent(next ? 'page_cache_on_submitted' : 'page_cache_off_submitted', {
      project_slug: project.slug,
    });
    setPageCache(project.id, next)
      .then((updated) => {
        setLocalOn(null);
        onChanged(updated);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : t("That didn't work. Try again."));
        captureEvent('page_cache_failed', {
          project_slug: project.slug,
          error_code: err instanceof ApiError ? err.code : 'unknown',
        });
      })
      .finally(() => setBusy(false));
  }

  return (
    <SectionPanel
      title={t('Page caching')}
      note={t(
        'We keep a copy of the pages that look the same for everyone and send that copy to the next visitor, so the page opens faster and your app runs less often.',
      )}
    >
      <div className="set__control">
        {error ? (
          <Notice tone="attention">
            <p>{error}</p>
          </Notice>
        ) : null}

        {on ? (
          <p className="set__say">
            {t(
              'Page caching is on. Every deploy starts with an empty cache, so a change you push shows up right away.',
            )}
          </p>
        ) : (
          <>
            {/* Said before the button, not after it. The risk is the whole
                reason this is a decision rather than a default. */}
            <p className="set__say">
              {t(
                'Turn this on only after your app marks which pages are the same for everyone. A page with no marking is kept for two hours and shown to every visitor, even a page that shows one person their own account.',
              )}
            </p>
            <p className="set__say">{t('Ask your coding agent to check first:')}</p>
            <CopyField label={t('What to ask your coding agent')} value={AGENT_ASK} />
          </>
        )}

        {paused ? (
          <p className="set__say">{t('Turn the project back on to change this.')}</p>
        ) : null}

        <div className="set__actions">
          <Button variant="outline" disabled={busy || paused} onClick={() => toggle(!on)}>
            {busy
              ? on
                ? t('Turning off')
                : t('Turning on')
              : on
                ? t('Turn off page caching')
                : t('Turn on page caching')}
          </Button>
        </div>
      </div>
    </SectionPanel>
  );
}
